import type { TimeEntry } from '@/domain/time-entry/TimeEntry.js';

export type EntryField =
  | 'clock_in'
  | 'clock_out'
  | 'unpaid_break_minutes'
  | 'break_minutes'
  | 'entry_type'
  | 'job_title'
  | 'notes'
  | 'workplace_id';

const SYNONYMS: Record<EntryField, string[]> = {
  break_minutes: ['break_minutes', 'break', 'break minutes'],
  clock_in: ['clock_in', 'clock in', 'shift start', 'start time', 'punch in', 'start'],
  clock_out: ['clock_out', 'clock out', 'shift end', 'end time', 'punch out', 'end'],
  entry_type: ['entry_type', 'type', 'shift type', 'entry type'],
  job_title: ['job_title', 'job title', 'job', 'position', 'role'],
  notes: ['notes', 'note', 'comment', 'comments', 'description'],
  unpaid_break_minutes: ['unpaid_break_minutes', 'unpaid break'],
  workplace_id: ['workplace_id', 'workplace', 'employer', 'location'],
};

export type ColMap = Partial<Record<EntryField, number>>;

export function detectColumnMap(headers: string[]): ColMap {
  const map: ColMap = {};
  headers.forEach((raw, i) => {
    const norm = raw.toLowerCase().trim();
    for (const [field, synonyms] of Object.entries(SYNONYMS) as [EntryField, string[]][]) {
      if (field in map) continue;
      if (synonyms.includes(norm)) map[field] = i;
    }
  });
  return map;
}

export function parseDateTime(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const s = String(raw).trim();
  let d = new Date(s);
  if (!Number.isNaN(d.getTime())) return d.toISOString();
  const mdy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(s);
  if (mdy) {
    d = new Date(+mdy[3], +mdy[1] - 1, +mdy[2], +mdy[4], +mdy[5], +(mdy[6] ?? 0));
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  return null;
}

export type ValidationResult =
  { error: string; valid: false } | { entry: Partial<TimeEntry>; valid: true };

function parseClockField(
  rawRow: string[],
  colMap: ColMap,
  field: EntryField,
  errorLabel: string,
): { error: string | null; ms: number } {
  const idx = colMap[field];
  const raw = idx === undefined ? undefined : rawRow[idx];
  const iso = parseDateTime(raw);
  if (!iso) return { error: `invalid ${errorLabel}`, ms: 0 };
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? { error: `invalid ${errorLabel}`, ms: 0 } : { error: null, ms };
}

function parseBreakMinutes(
  rawRow: string[],
  colMap: ColMap,
): { error: string | null; minutes: number } {
  const get = (f: EntryField) => {
    const idx = colMap[f];
    return idx === undefined ? undefined : rawRow[idx];
  };
  const val = get('break_minutes') ?? get('unpaid_break_minutes');
  const minutes = Number.parseInt(val ?? '0', 10);
  return Number.isNaN(minutes) || minutes < 0
    ? { error: 'invalid break_minutes', minutes: 0 }
    : { error: null, minutes };
}

export function validateRow(rawRow: string[], colMap: ColMap): ValidationResult {
  const get = (f: EntryField) => {
    const idx = colMap[f];
    return idx === undefined ? undefined : rawRow[idx];
  };

  const clockIn = parseClockField(rawRow, colMap, 'clock_in', 'clock_in');
  if (clockIn.error) return { error: clockIn.error, valid: false };
  const clockOut = parseClockField(rawRow, colMap, 'clock_out', 'clock_out');
  if (clockOut.error) return { error: clockOut.error, valid: false };
  if (clockOut.ms <= clockIn.ms) return { error: 'clock_out not after clock_in', valid: false };
  const breakResult = parseBreakMinutes(rawRow, colMap);
  if (breakResult.error) return { error: breakResult.error, valid: false };
  return {
    entry: {
      break_minutes: breakResult.minutes,
      ends_at: clockOut.ms,
      entry_type: get('entry_type') || 'shift',
      job_title: get('job_title') || '',
      notes: get('notes') || '',
      starts_at: clockIn.ms,
    },
    valid: true,
  };
}

export function overlaps(a: Partial<TimeEntry>, b: TimeEntry): boolean {
  return (a.starts_at ?? 0) < (b.ends_at ?? 0) && (a.ends_at ?? 0) > (b.starts_at ?? 0);
}

export interface ImportOptions {
  defaultWorkplaceId?: string | null;
  strategy?: 'skip' | 'overwrite' | 'keep';
}

export interface ImportResult {
  errors: string[];
  imported: number;
  skipped: number;
}
